import * as React from "react";
import fetchWrapper from "../utils/fetch";

const AuthContext = React.createContext();

// Helper function to decode JWT token
function decodeJWT(token) {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(atob(base64).split('').map(c => 
      '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2)
    ).join(''));
    return JSON.parse(jsonPayload);
  } catch (err) {
    return null;
  }
}

function authReducer(state, action) {
  switch (action.type) {
    case "start login": {
      return {
        ...state,
        status: "loading",
      };
    }
    case "finished login": {
      localStorage.setItem("access_token", action.payload);
      return {
        ...state,
        status: "succeed",
        isAuthenticated: true,
        hasTemporaryToken: false,
        user: null, // Will be populated by profile fetch
        requested: true,
      };
    }
    case "failed login": {
      localStorage.removeItem("access_token");
      return {
        ...state,
        status: "error",
        isAuthenticated: false,
        hasTemporaryToken: false,
        user: null,
        requested: true,
      };
    }

    case "start profile": {
      return {
        ...state,
        status: "loading",
      };
    }
    case "finished profile": {
      return {
        ...state,
        status: "succeed",
        isAuthenticated: true,
        user: action.payload,
        hasTemporaryToken: false,
        requested: true,
      };
    }
    case "failed profile": {
      localStorage.removeItem("access_token");
      return {
        ...state,
        status: "error",
        isAuthenticated: false,
        user: null,
        hasTemporaryToken: false,
        error: action.payload,
        requested: true,
      };
    }

    case "logout": {
      localStorage.removeItem("access_token");
      return {
        ...state,
        isAuthenticated: false,
        user: null,
        hasTemporaryToken: false,
        requested: true,
      };
    }

    case "set temporary token": {
      localStorage.setItem("access_token", action.payload);
      return {
        ...state,
        isAuthenticated: false,
        hasTemporaryToken: true,
        user: {
          is_temporary: true,
          first_name: "anonymous",
          last_name: "user",
          email: "",
          is_admin: false,
          is_pro: false,
        },
        requested: true,
      };
    }

    case "clear temporary token": {
      localStorage.removeItem("access_token");
      return {
        ...state,
        isAuthenticated: false,
        hasTemporaryToken: false,
        user: null,
        requested: true,
      };
    }

    default: {
      throw new Error(`Unhandled action type: ${action.type}`);
    }
  }
}

function AuthProvider({ children }) {
  const [state, dispatch] = React.useReducer(authReducer, {
    isAuthenticated: false,
    hasTemporaryToken: false,
    user: null,
    status: "init",
    requested: false,
    error: null,
  });

  // Initialize auth state with existing token
  React.useEffect(() => {
    const token = localStorage.getItem("access_token");
    if (!token) {
      dispatch({ type: "clear temporary token" });
      return;
    }

    try {
      const decodedToken = decodeJWT(token);
      
      if (!decodedToken || !decodedToken.username) {
        localStorage.removeItem("access_token");
        dispatch({ type: "clear temporary token" });
        return;
      }

      if (decodedToken.username.startsWith("anony-")) {
        dispatch({ type: "set temporary token", payload: token });
        return;
      }

      // Fetch profile for authenticated users
      dispatch({ type: "start profile" });
      fetchWrapper
        .get("/profile")
        .then((data) => dispatch({ type: "finished profile", payload: data }))
        .catch((err) => {
          if (err.status === 401) {
            localStorage.removeItem("access_token");
          }
          dispatch({ type: "failed profile", payload: err });
        });
    } catch (err) {
      localStorage.removeItem("access_token");
      dispatch({ type: "clear temporary token" });
    }
  }, []); // Only run once on mount

  // Helper function to check if user can access protected features
  const canAccessProtectedFeatures = () => {
    return state.isAuthenticated || state.hasTemporaryToken;
  };

  const value = React.useMemo(
    () => [state, dispatch, { canAccessProtectedFeatures }],
    [state]
  );

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
}

function useAuth() {
  const context = React.useContext(AuthContext);
  if (context === undefined) {
    throw new Error("useAuth must be used within a AuthProvider");
  }
  return context;
}

export { AuthProvider, useAuth, decodeJWT };
